import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

export default function Checklists() {
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");

  const blankQ = () => ({ id: crypto.randomUUID(), text: "", qtype: "compliance", mandatory: true, criticality: "normal", remarks_required: false, image_allowed: true });

  const load = async () => {
    setLoading(true);
    const [{ data: cls }, { data: ar }, { data: us }] = await Promise.all([
      api.get("/checklists"), api.get("/masters/areas"), api.get("/users"),
    ]);
    setItems(cls); setAreas(ar);
    setStaffUsers(us.filter((u) => u.role === "staff" || u.role === "supervisor"));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({
      area_id: areas[0]?.id || "",
      name: "", frequency: "daily", category: "normal", active: true,
      auto_assign: false, default_assignee_id: "", tat_minutes: 60,
      questions: [blankQ()],
    });
    setModal({ item: null }); setErr("");
  };
  const openEdit = (it) => {
    setForm({
      ...it,
      auto_assign: !!it.auto_assign,
      default_assignee_id: it.default_assignee_id || "",
      tat_minutes: it.tat_minutes ?? 60,
    });
    setModal({ item: it }); setErr("");
  };

  const save = async () => {
    try {
      const payload = { ...form };
      // Normalise empty default_assignee_id to null
      if (!payload.default_assignee_id) payload.default_assignee_id = null;
      if (modal.item) await api.patch(`/checklists/${modal.item.id}`, payload);
      else await api.post("/checklists", payload);
      setModal(null); load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this checklist?")) return;
    await api.delete(`/checklists/${id}`);
    load();
  };

  const generateNow = async () => {
    try {
      const { data } = await api.post("/tasks/generate");
      alert(`Generated ${data.created} task${data.created === 1 ? "" : "s"} from auto-assign checklists.`);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updQ = (idx, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === idx ? { ...q, ...patch } : q) }));
  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, blankQ()] }));
  const rmQ = (idx) => setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));

  const areaName = (id) => areas.find((a) => a.id === id)?.name || "—";
  const userName = (id) => staffUsers.find((u) => u.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="checklists-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-small text-blue-700">Configuration</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>Checklists</h1>
          <p className="text-sm text-slate-600 mt-1">Configure cleaning checklists, TAT, auto-assignment and compliance questions.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="generate-tasks-button" onClick={generateNow} className="px-4 py-2 border border-blue-200 text-blue-700 hover:bg-blue-50 text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
            Generate auto tasks
          </button>
          <button data-testid="new-checklist-button" onClick={openCreate} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
            <Plus size={14} weight="bold" /> New checklist
          </button>
        </div>
      </header>

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
        <div className="flat-card">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Area</th><th>Frequency</th><th>TAT</th><th>Auto-Assign</th><th>Assignee</th><th>Questions</th><th></th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`checklist-row-${it.id}`}>
                  <td className="font-semibold text-slate-900">{it.name}</td>
                  <td>{areaName(it.area_id)}</td>
                  <td><span className="label-small">{it.frequency}</span></td>
                  <td className="text-xs font-mono">{it.tat_minutes ?? "—"} min</td>
                  <td>{it.auto_assign ? <span className="label-small text-green-700">ON</span> : <span className="label-small text-slate-400">off</span>}</td>
                  <td className="text-xs">{it.default_assignee_id ? userName(it.default_assignee_id) : "—"}</td>
                  <td>{it.questions?.length || 0}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(it)} className="p-1.5 rounded text-slate-500 hover:text-blue-600 transition-colors"><PencilSimple size={15} /></button>
                    <button onClick={() => remove(it.id)} className="p-1.5 rounded text-slate-500 hover:text-red-600 transition-colors"><Trash size={15} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-8">No checklists yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-md max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="checklist-modal">
            <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>{modal.item ? "Edit" : "New"} Checklist</h3>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="col-span-2">
                <label className="label-small block mb-1">Name</label>
                <input data-testid="cl-name" value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
              </div>
              <div>
                <label className="label-small block mb-1">Area</label>
                <select data-testid="cl-area" value={form.area_id || ""} onChange={(e) => setForm((f) => ({ ...f, area_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
                  {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-small block mb-1">Frequency</label>
                <select value={form.frequency || "daily"} onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
                  {["hourly", "daily", "weekly", "monthly", "custom"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="label-small block mb-1">Category</label>
                <select value={form.category || "normal"} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
                  {["critical", "normal", "optional"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="label-small block mb-1">TAT (minutes)</label>
                <input data-testid="cl-tat" type="number" value={form.tat_minutes ?? ""} onChange={(e) => setForm((f) => ({ ...f, tat_minutes: e.target.value === "" ? null : parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" placeholder="e.g. 60" />
              </div>
            </div>

            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 mb-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
                <input type="checkbox" data-testid="cl-auto-assign" checked={!!form.auto_assign} onChange={(e) => setForm((f) => ({ ...f, auto_assign: e.target.checked }))} />
                Auto-assign tasks per frequency
              </label>
              <p className="text-xs text-slate-500 mb-2">When enabled, the "Generate auto tasks" action creates a pending task in each frequency window if none is already open.</p>
              {form.auto_assign && (
                <div>
                  <label className="label-small block mb-1">Default assignee</label>
                  <select data-testid="cl-default-assignee" value={form.default_assignee_id || ""} onChange={(e) => setForm((f) => ({ ...f, default_assignee_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
                    <option value="">— Unassigned (anyone can pick up) —</option>
                    {staffUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="divider my-4" />

            <div className="flex items-center justify-between mb-3">
              <div className="label-small">Questions</div>
              <button onClick={addQ} data-testid="add-question-button" className="text-xs font-semibold text-blue-600 hover:underline">+ Add question</button>
            </div>
            <div className="space-y-3">
              {(form.questions || []).map((q, idx) => (
                <div key={q.id || idx} className="border border-slate-200 rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-slate-500">Q{idx + 1}</div>
                    <button onClick={() => rmQ(idx)} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                  </div>
                  <input data-testid={`q-text-${idx}`} value={q.text} onChange={(e) => updQ(idx, { text: e.target.value })} placeholder="Question text" className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
                  <div className="grid grid-cols-3 gap-2">
                    <select value={q.qtype} onChange={(e) => updQ(idx, { qtype: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white" data-testid={`q-qtype-${idx}`}>
                      <option value="compliance">Compliant / Non-compliant / Partial</option>
                      <option value="yes_no">Yes / No</option>
                      <option value="numeric">Numeric</option>
                      <option value="remarks">Remarks</option>
                    </select>
                    <select value={q.criticality} onChange={(e) => updQ(idx, { criticality: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white">
                      <option value="critical">Critical</option>
                      <option value="normal">Normal</option>
                      <option value="optional">Optional</option>
                    </select>
                    <label className="flex items-center gap-2 text-xs text-slate-700"><input type="checkbox" checked={q.mandatory} onChange={(e) => updQ(idx, { mandatory: e.target.checked })} /> Mandatory</label>
                  </div>
                </div>
              ))}
            </div>

            {err && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

            <div className="flex justify-end gap-2 pt-5 mt-4 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors">Cancel</button>
              <button data-testid="checklist-save" onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
